/** @prettier */
import React, { useMemo } from "react";
import "./TestRunner.css";
import classNames from "classnames";
import TestSummaryBar from "./TestSummaryBar";
import ActionBar from "./ActionBar";
import {
    useTestRunnerState,
    TestRunnerLoadedState,
    TestRunnerDispatch,
    getSummaryState,
    getCurrentQuestion,
    getAnsweredQuestionCount,
    QuestionDataWithImages,
} from "./testRunnerState";
import useTestRunnerKeyboardShortcuts from "./useTestRunnerKeyboardShortcuts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FinishTestView from "./FinishTestView";
import { TestTimer } from "./TestTimer";
import Image from "../View/Image";
import TestView from "../View/TestView";

// Handles loading spinner state
const TestRunnerWrapper = ({ id }: { id: string }) => {
    const [state, dispatch] = useTestRunnerState(id);
    return state.state === "loaded" ? (
        <TestRunner state={state} dispatch={dispatch} />
    ) : (
        <div className="test-view">
            <div className="tr-loading">
                <FontAwesomeIcon icon="spinner" size="4x" spin />
            </div>
        </div>
    );
};
export default TestRunnerWrapper;

type TestRunnerProps = {
    state: TestRunnerLoadedState;
    dispatch: TestRunnerDispatch;
};
const TestRunner = ({ state, dispatch }: TestRunnerProps) => {
    useTestRunnerKeyboardShortcuts(dispatch);
    const questionNumber = state.currentQuestionIndex;
    const summaryState = useMemo(() => getSummaryState(state), [state]);
    const currentQuestion = getCurrentQuestion(state);
    return (
        <TestView
            id="test-runner"
            titleBar={
                <div className="tr-title-bar">
                    <span className="tr-title">{state.testName}</span>
                    {state.testType.type === "TIMED" ? (
                        <TestTimer
                            deadline={state.testType.deadline}
                            dispatch={dispatch}
                        />
                    ) : undefined}
                </div>
            }
            percent={questionNumber / state.totalQuestionCount}
            summaryBar={
                <TestSummaryBar
                    dispatch={dispatch}
                    questionNumber={questionNumber}
                    totalQuestionCount={state.totalQuestionCount}
                    summaryState={summaryState}
                />
            }
            actionBar={
                <ActionBar
                    questionIsFlagged={currentQuestion?.flagged ?? false}
                    dispatch={dispatch}
                />
            }
        >
            {state.timeExpired ? (
                <div className="tr-main-content">
                    <p className="tr-content-heading">Time's up!</p>
                    <div>
                        Questions answered: {getAnsweredQuestionCount(state)}/
                        {state.totalQuestionCount}
                    </div>
                </div>
            ) : currentQuestion ? (
                <Question
                    question={currentQuestion}
                    totalQuestionCount={state.totalQuestionCount}
                    questionNumber={questionNumber}
                    dispatch={dispatch}
                />
            ) : (
                <FinishTestView
                    questionsAnswered={getAnsweredQuestionCount(state)}
                    totalQuestionCount={state.totalQuestionCount}
                    dispatch={dispatch}
                />
            )}
        </TestView>
    );
};

//==========
// Question
//==========
const answerLabel = (index: number) =>
    String.fromCharCode("A".charCodeAt(0) + index);

type QuestionProps = {
    question: QuestionDataWithImages;
    questionNumber: number;
    totalQuestionCount: number;
    dispatch: TestRunnerDispatch;
};
const Question = ({
    question,
    questionNumber,
    totalQuestionCount,
    dispatch,
}: QuestionProps) => (
    <div className="tr-main-content tr-question">
        <p className="tr-content-heading">
            Question {questionNumber + 1} of {totalQuestionCount}
        </p>
        <p className="tr-question-text">{question.text}</p>
        <div className="tr-question-images">
            {question.images?.state === "loaded" &&
                question.images.stemImages.map(({ image, fileName }) => (
                    <Image name={fileName} image={image} />
                ))}
        </div>
        {question.answerChoices.map((choice, index) => (
            <AnswerChoice
                key={index}
                onClick={() =>
                    dispatch({ event: "selectAnswer", answer: index })
                }
                selected={index === question.selectedAnswer}
            >
                {`${answerLabel(index)}. ${choice}`}
            </AnswerChoice>
        ))}
    </div>
);

type AnswerChoiceProps = {
    selected?: boolean;
    children: string;
    onClick: () => void;
};
const AnswerChoice = ({ selected, children, onClick }: AnswerChoiceProps) => (
    <button
        className={classNames("tr-answer", { selected })}
        onClick={onClick}
        disabled={selected}
    >
        {children}
    </button>
);
